// Workaround for: https://github.com/bazelbuild/rules_nodejs/issues/1265
/// <reference types="youtube" />

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { OnChange } from '@df/ng/on-change';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { AbstractPlayerComponent } from '@df/ui/display/media/abstract-player.component';
import {
    BehaviorSubject,
    combineLatest,
    ConnectableObservable,
    fromEventPattern,
    merge,
    MonoTypeOperatorFunction,
    Observable,
    of as observableOf,
    of,
    OperatorFunction,
    pipe,
    Subject
} from 'rxjs';
import {
    combineLatest as combineLatestOp,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    publish,
    scan,
    skipWhile,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

declare global {
    interface Window {
        YT: typeof YT | undefined;
        onYouTubeIframeAPIReady: (() => void) | undefined;
    }
}

// The native YT.Player doesn't expose the set videoId, but we need it for
// convenience.
interface Player extends YT.Player {
    videoId?: string;
    playerVars?: YT.PlayerVars;
}

// The player isn't fully initialized when it's constructed.
// The only field available is destroy and addEventListener.
type UninitializedPlayer = Pick<Player, 'videoId' | 'playerVars' | 'destroy' | 'addEventListener'>;

/**
 * Object used to store the state of the player if the
 * user tries to interact with the API before it has been loaded.
 */
interface PendingPlayerState {
    playbackState?: YT.PlayerState.PLAYING | YT.PlayerState.PAUSED | YT.PlayerState.CUED;
    playbackRate?: number;
    volume?: number;
    muted?: boolean;
    seek?: { seconds: number; allowSeekAhead: boolean };
}

const YT_DEFAULT_OPTIONS: YT.PlayerVars = {
    cc_load_policy: 0,
    rel: 0,
    enablejsapi: 1
};

let uid = 1;

/**
 * Angular component that renders a YouTube player via the YouTube player
 * iframe API.
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
@Component({
    selector: 'youtube-player',
    template: `
        <div [attr.id]="playerId" #youtubeContainer></div>
        @if ((!background && ((!autoplay && !autoplayInViewport) || isPlaying || showControl)) || controls) {
            <div class="video-player-control" [class.is-playing]="isPlaying">
                @if (playButton && !isPlaying) {
                    <div class="icon-control icon-video-play" (click)="play()">
                        <ng-container *ngTemplateOutlet="playButton; context: { $implicit: this }"></ng-container>
                    </div>
                }
                @if (pauseButton && isPlaying) {
                    <div class="icon-control icon-video-pause" (click)="pause()">
                        <ng-container *ngTemplateOutlet="pauseButton; context: { $implicit: this }"></ng-container>
                    </div>
                }
            </div>
        }
        <ng-container *ngTemplateOutlet="customTpl!; context: { $implicit: this }"></ng-container>
    `,
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { ngSkipHydration: 'true' }
})
export class YoutubePlayerComponent extends AbstractPlayerComponent implements AfterViewInit, OnDestroy, OnInit {
    protected static PLAYER_API_URL = '//www.youtube.com/iframe_api';

    private afterContentInitService = inject(AfterContentInitDirectiveService);

    private readonly _youtubeContainer = new Subject<HTMLElement>();
    private readonly _destroyed = new Subject<void>();
    private _player: Player | undefined;
    private _existingApiReadyCallback: (() => void) | undefined;
    private _pendingPlayerState: PendingPlayerState | undefined;
    private readonly _playerChanges = new BehaviorSubject<UninitializedPlayer | undefined>(undefined);
    private readonly _videoId = new BehaviorSubject<string | undefined>(undefined);

    protected readonly playerId = `yt-player-${uid++}`;

    /** YouTube Video ID to view */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('video-id')
    @OnChange<string>(function (this: YoutubePlayerComponent, value: string) {
        this._videoId.next(parseId(value));
    })
    declare videoId?: string;

    /**
     * Video URL.
     *
     * @type {string}
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('video-url')
    @OnChange<string>(function (this: YoutubePlayerComponent, value: string) {
        this._videoId.next(parseId(value));
    })
    declare videoUrl?: string;

    /**
     * Display video as background - autoplay, loop and no controls - available only for Plus, PRO or Business members.
     *
     * @type {boolean}
     */
    @Input()
    @OnChange<boolean>(function (this: YoutubePlayerComponent, value: boolean) {
        if (value) {
            const playerVars = this.playerVars;
            const newPlayerVars: Partial<YT.PlayerVars> = {
                disablekb: 1,
                fs: 0,
                iv_load_policy: 3,
                showinfo: 0
            };

            if (this.autoplay === undefined) {
                newPlayerVars.autoplay = 1;
            }

            if (this.controls === undefined) {
                newPlayerVars.controls = 0;
            }

            if (this.loop === undefined) {
                newPlayerVars.loop = 1;
            }

            this.muted = this.muted !== undefined ? this.muted : coerceBooleanProperty(value);

            this.playerVars = {
                ...playerVars,
                ...newPlayerVars
            };
        }

        this.setBackgroundMode(coerceBooleanProperty(value));
    })
    background?: boolean;

    @Input()
    @OnChange<boolean>(function (this: YoutubePlayerComponent, value: boolean) {
        const playerVars = this.playerVars;

        this.playerVars = {
            ...playerVars,
            autoplay: value ? 1 : 0
        };
    })
    declare autoplay?: boolean;

    @Input()
    @OnChange<boolean>(function (this: YoutubePlayerComponent, value: boolean) {
        const playerVars = this.playerVars;

        this.playerVars = {
            ...playerVars,
            controls: value ? 1 : 0
        };
    })
    controls?: boolean;

    @Input()
    @OnChange<boolean>(function (this: YoutubePlayerComponent, value: boolean) {
        if (value) {
            this.mute();
        } else {
            this.unMute();
        }
    })
    muted?: boolean;

    @Input()
    @OnChange<boolean>(function (this: YoutubePlayerComponent, value: boolean) {
        const playerVars = this.playerVars;

        this.playerVars = {
            ...playerVars,
            loop: value ? 1 : 0
        };
    })
    declare loop?: boolean;

    /** The moment when the player is supposed to start playing */
    @Input()
    set startSeconds(startSeconds: number | undefined) {
        this._startSeconds.next(startSeconds);
    }
    private readonly _startSeconds = new BehaviorSubject<number | undefined>(undefined);

    /** The moment when the player is supposed to stop playing */
    @Input()
    set endSeconds(endSeconds: number | undefined) {
        this._endSeconds.next(endSeconds);
    }
    private readonly _endSeconds = new BehaviorSubject<number | undefined>(undefined);

    /** The suggested quality of the player */
    @Input()
    set suggestedQuality(suggestedQuality: YT.SuggestedVideoQuality | undefined) {
        this._suggestedQuality.next(suggestedQuality);
    }
    private readonly _suggestedQuality = new BehaviorSubject<YT.SuggestedVideoQuality | undefined>(undefined);

    /**
     * Extra parameters used to configure the player. See:
     * https://developers.google.com/youtube/player_parameters.html?playerVersion=HTML5#Parameters
     */
    @Input()
    set playerVars(playerVars: YT.PlayerVars | undefined) {
        this._playerVars.next({ ...YT_DEFAULT_OPTIONS, origin: this.app?.url?.toUrl(''), ...playerVars });
    }
    get playerVars(): YT.PlayerVars | undefined {
        return this._playerVars.value;
    }
    private _playerVars = new BehaviorSubject<YT.PlayerVars | undefined>({
        ...YT_DEFAULT_OPTIONS
    });

    /**
     * Whether the iframe will attempt to load regardless of the status of the api on the
     * page. Set this to true if you don't want the `onYouTubeIframeAPIReady` field to be
     * set on the global window.
     */
    @Input()
    showBeforeIframeApiLoads: boolean | undefined;

    /** Outputs are direct proxies from the player itself. */
    @Output()
    readonly ready: Observable<YT.PlayerEvent> = this._getLazyEmitter<YT.PlayerEvent>('onReady');

    @Output()
    readonly stateChange: Observable<YT.OnStateChangeEvent> = this._getLazyEmitter<YT.OnStateChangeEvent>('onStateChange');

    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly error: Observable<YT.OnErrorEvent> = this._getLazyEmitter<YT.OnErrorEvent>('onError');

    @Output()
    readonly apiChange: Observable<YT.PlayerEvent> = this._getLazyEmitter<YT.PlayerEvent>('onApiChange');

    @Output()
    readonly playbackQualityChange: Observable<YT.OnPlaybackQualityChangeEvent> =
        this._getLazyEmitter<YT.OnPlaybackQualityChangeEvent>('onPlaybackQualityChange');

    @Output()
    readonly playbackRateChange: Observable<YT.OnPlaybackRateChangeEvent> =
        this._getLazyEmitter<YT.OnPlaybackRateChangeEvent>('onPlaybackRateChange');

    @HostBinding('class.video-player-container')
    videoPlayerContainer = true;

    @OnChange(function (this: YoutubePlayerComponent, value: boolean) {
        value
            ? this.renderer2?.addClass(this.elementRef?.nativeElement, 'is-playing')
            : this.renderer2?.removeClass(this.elementRef?.nativeElement, 'is-playing');
    })
    override isPlaying = false;

    @OnChange(function (this: YoutubePlayerComponent, value: boolean) {
        value
            ? this.renderer2?.addClass(this.elementRef?.nativeElement, 'is-paused')
            : this.renderer2?.removeClass(this.elementRef?.nativeElement, 'is-paused');
    })
    override isPaused = true;

    @OnChange(function (this: YoutubePlayerComponent, value: boolean) {
        value
            ? this.renderer2?.addClass(this.elementRef?.nativeElement, 'is-muted')
            : this.renderer2?.removeClass(this.elementRef?.nativeElement, 'is-muted');
    })
    override isMuted = false;

    // If pause show play button
    @OnChange(function (this: YoutubePlayerComponent, value: boolean) {
        value
            ? this.renderer2?.addClass(this.elementRef?.nativeElement, 'has-controls')
            : this.renderer2?.removeClass(this.elementRef?.nativeElement, 'has-controls');
    })
    override showControl = false;

    /** The element that will be replaced by the iframe. */
    @ViewChild('youtubeContainer')
    set youtubeContainer(value: ElementRef<HTMLElement>) {
        this._youtubeContainer.next(value.nativeElement);
    }

    private playAfterBufferingInterval?: number;

    private renderer2 = inject(Renderer2);

    override ngOnInit() {
        // Don't do anything if we're not in a browser environment.
        if (!this.app.isPlatformBrowser) {
            return;
        }

        let iframeApiAvailableObs: Observable<boolean> = observableOf(true);
        if (!window.YT || !window.YT.Player) {
            const iframeApiAvailableSubject = new Subject<boolean>();
            this._existingApiReadyCallback = window.onYouTubeIframeAPIReady;

            window.onYouTubeIframeAPIReady = () => {
                if (this._existingApiReadyCallback) {
                    this._existingApiReadyCallback();
                }
                this.timeoutService.ngZone.run(() => iframeApiAvailableSubject.next(true));
            };
            iframeApiAvailableObs = iframeApiAvailableSubject.pipe(take(1), startWith(false));
        }

        // An observable of the currently loaded player.
        const playerObs = createPlayerObservable(
            this._youtubeContainer,
            this._videoId,
            iframeApiAvailableObs,
            this._playerVars,
            this.timeoutService.ngZone
        ).pipe(
            tap(player => {
                // Emit this before the `waitUntilReady` call so that we can bind to
                // events that happen as the player is being initialized (e.g. `onReady`).
                this._playerChanges.next(player);
            }),
            waitUntilReady(player => {
                // Destroy the player if loading was aborted so that we don't end up leaking memory.
                if (!playerIsReady(player)) {
                    player.destroy();
                }
            }),
            takeUntil(this._destroyed),
            publish()
        );

        // Set up side effects to bind inputs to the player.
        playerObs.subscribe(player => {
            this._player = player;

            if (player && this._pendingPlayerState) {
                this._initializePlayer(player, this._pendingPlayerState);
            }

            this._pendingPlayerState = undefined;
        });

        bindSuggestedQualityToPlayer(playerObs, this._suggestedQuality);

        bindCueVideoCall(playerObs, this._videoId, this._startSeconds, this._endSeconds, this._suggestedQuality, this._destroyed);

        // After all of the subscriptions are set up, connect the observable.
        (playerObs as ConnectableObservable<Player>).connect();

        this.ready.pipe(takeUntil(this._destroyed)).subscribe(() => this.playIfReady(() => this.setControlsListeners()));

        this.stateChange
            .pipe(takeUntil(this._destroyed))
            .subscribe(event => this.onPlayerStateChange(event, () => this.setControlsListeners()));

        this.error.subscribe(err => this.app.logger.error(err));

        super.ngOnInit();
    }

    override ngOnDestroy() {
        if (this._player) {
            this._player.destroy();
            window.onYouTubeIframeAPIReady = this._existingApiReadyCallback;
        }

        this._playerChanges.complete();
        this._videoId.complete();
        this._startSeconds.complete();
        this._endSeconds.complete();
        this._suggestedQuality.complete();
        this._youtubeContainer.complete();
        this._playerVars.complete();
        this._destroyed.next();
        this._destroyed.complete();

        super.ngOnDestroy();
    }

    protected override load(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        // a background video exists only to autoplay, so honour the user asking for less motion or less
// data, and skip it on a phone - forceLoad is the explicit opt in (eg a tap to play)
        if (this.background && this.prefersNoAutoplay && !this.forceLoad) {
            return;
        }

        super.load();

        // the base class already waits for the viewport and only fires once, so the player stays
        // loaded after scrolling away - hold the API script back until the page content is initialised
        this.subscriptions.add(
            this.afterContentInitService.loaded
                .pipe(
                    filter<boolean>(loaded => loaded),
                    take(1)
                )
                .subscribe(() => this.requestService.loadScript(YoutubePlayerComponent.PLAYER_API_URL))
        );
    }

    /**
     * If autoplay in viewport or autoplay.
     *
     * @returns {boolean}
     */
    protected override canPlay(): boolean {
        return !!(
            !this.prefersNoAutoplay &&
            this._player &&
            !!(this.autoplayInViewport || this.autoplay || this.playerVars?.autoplay === 1) &&
            ((this.autoplayOnPause && (this.isPaused === undefined || this.isPaused === true)) ||
                (!this.autoplayOnPause && this.isPaused === undefined))
        );
    }

    protected playIfReady(callback?: () => void): void {
        if (this.muted) {
            this.mute();
        }

        // if video is in background mode than pause it on init and autoplay in viewport
        if (this.background) {
            this.mute();
            this.seekTo(0, true);

            if (!this.useCustomControls) {
                this.checkInView();
            }
        } else if (!this.useCustomControls) {
            this.checkInView();
        } else if (callback) {
            callback();
        }
    }

    /**
     * Player state
     * -1 – unstarted
     * 0 - ended
     * 1 - playing
     * 2 - paused
     * 3 - buffering
     * 5 - video cued
     */
    protected onPlayerStateChange(event: YT.OnStateChangeEvent, callback?: () => void): void {
        switch (event.data) {
            case -1:
                if (this.isPlaying) {
                    this.play();
                } else {
                    this.playIfReady(callback);
                }

                break;
            case 0:
                if (this.background) {
                    this.seekTo(0, true);
                }

                this.isPaused = false;

                if (this.playerStop) {
                    this.playerStop.emit(this);
                }

                break;
            case 1:
                if (this.background) {
                    this.setBackgroundMode(true);
                }

                this.isPlaying = true;
                this.isPaused = false;
                this.showControl = false;

                if (this.playerPlay) {
                    this.playerPlay.emit(this);
                }

                this.play();

                break;
            case 2:
                this.clearPlayAfterBufferingInterval();
                this.isPlaying = false;
                this.isPaused = true;
                this.showControl = true;

                if (this.playerPause) {
                    this.playerPause.emit(this);
                }

                if (this.resetOnPause) {
                    this.seekTo(0, true);
                }

                break;
            case 3:
                this.isPlaying = false;
                this.isPaused = true;
                this.showControl = true;

                this.setBackgroundMode(false);

                this.playAfterBuffering();

                break;
        }
    }

    protected playAfterBuffering() {
        this.clearPlayAfterBufferingInterval();
        this.playAfterBufferingInterval = this.timeoutService.setInterval(() => {
            if (this.getPlayerState() !== 1) {
                this.play();
            } else {
                this.clearPlayAfterBufferingInterval();
            }
        }, 5000);
    }

    private clearPlayAfterBufferingInterval(): void {
        clearInterval(this.playAfterBufferingInterval);
    }

    protected setBackgroundMode(to: boolean) {
        this.renderer2.setStyle(this.elementRef.nativeElement, 'pointerEvents', to ? 'none' : null);
        to
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'is-background')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'is-background');
    }

    override play(inView = false): void {
        this.playVideo();
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#playVideo */
    playVideo() {
        if (this._player) {
            this._player.playVideo();
        } else {
            this._getPendingState().playbackState = window.YT ? YT.PlayerState.PLAYING : 1;
        }
    }

    override pause(inView = false): void {
        this.pauseVideo();
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#pauseVideo */
    pauseVideo() {
        if (this._player) {
            this._player.pauseVideo();
        } else {
            this._getPendingState().playbackState = window.YT ? YT.PlayerState.PAUSED : 2;
        }
    }

    override stop(): void {
        this.stopVideo();
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#stopVideo */
    stopVideo() {
        if (this._player) {
            this._player.stopVideo();
        } else {
            // It seems like YouTube sets the player to CUED when it's stopped.
            this._getPendingState().playbackState = window.YT ? YT.PlayerState.CUED : 5;
        }
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#seekTo */
    seekTo(seconds: number, allowSeekAhead: boolean) {
        if (this._player) {
            this._player.seekTo(seconds, allowSeekAhead);
        } else {
            this._getPendingState().seek = { seconds, allowSeekAhead };
        }
    }

    override mute(): void {
        this.muteVideo();
        this.isMuted = true;

        this.detectChanges();
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#mute */
    muteVideo() {
        if (this._player) {
            this._player.mute();
        } else {
            this._getPendingState().muted = true;
        }
    }

    override unMute(): void {
        this.unMuteVideo();
        this.isMuted = false;
        this.detectChanges();
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#unMute */
    unMuteVideo() {
        if (this._player) {
            this._player.unMute();
        } else {
            this._getPendingState().muted = false;
        }
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#setVolume */
    setVolume(volume: number) {
        if (this._player) {
            this._player.setVolume(volume);
        } else {
            this._getPendingState().volume = volume;
        }
    }

    /** See https://developers.google.com/youtube/iframe_api_reference#getPlayerState */
    getPlayerState(): YT.PlayerState | undefined {
        if (!this.app.isPlatformBrowser || !window.YT) {
            return undefined;
        }

        if (this._player) {
            return this._player.getPlayerState();
        }

        if (this._pendingPlayerState && this._pendingPlayerState.playbackState != null) {
            return this._pendingPlayerState.playbackState;
        }

        return YT.PlayerState.UNSTARTED;
    }

    /** Gets an object that should be used to store the temporary API state. */
    private _getPendingState(): PendingPlayerState {
        if (!this._pendingPlayerState) {
            this._pendingPlayerState = {};
        }

        return this._pendingPlayerState;
    }

    /** Initializes a player from a temporary state. */
    private _initializePlayer(player: YT.Player, state: PendingPlayerState): void {
        const { playbackState, playbackRate, volume, muted, seek } = state;

        switch (playbackState) {
            case YT.PlayerState.PLAYING:
                player.playVideo();
                break;
            case YT.PlayerState.PAUSED:
                player.pauseVideo();
                break;
            case YT.PlayerState.CUED:
                player.stopVideo();
                break;
        }

        if (playbackRate != null) {
            player.setPlaybackRate(playbackRate);
        }

        if (volume != null) {
            player.setVolume(volume);
        }

        if (muted != null) {
            muted ? player.mute() : player.unMute();
        }

        if (seek != null) {
            player.seekTo(seek.seconds, seek.allowSeekAhead);
        }
    }

    /** Gets an observable that adds an event listener to the player when a user subscribes to it. */
    private _getLazyEmitter<T extends YT.PlayerEvent>(name: keyof YT.Events): Observable<T> {
        // Start with the stream of players. This way the events will be transferred
        // over to the new player if it gets swapped out under-the-hood.
        return this._playerChanges.pipe(
            // Switch to the bound event. `switchMap` ensures that the old event is removed when the
            // player is changed. If there's no player, return an observable that never emits.
            switchMap(player => {
                return player
                    ? fromEventPattern<T>(
                          (listener: (event: T) => void) => {
                              player.addEventListener(name, listener);
                          },
                          (listener: (event: T) => void) => {
                              // The API seems to throw when we try to unbind from a destroyed player and it doesn't
                              // expose whether the player has been destroyed so we have to wrap it in a try/catch to
                              // prevent the entire stream from erroring out.
                              try {
                                  if ((player as Player).removeEventListener) {
                                      (player as Player).removeEventListener(name, listener);
                                  }
                              } catch (err) {
                                  this.app.logger.error(err);
                              }
                          }
                      )
                    : observableOf<T>();
            }),
            // By default we run all the API interactions outside the zone
            // so we have to bring the events back in manually when they emit.
            (source: Observable<T>) =>
                new Observable<T>(observer =>
                    source.subscribe({
                        next: value => this.timeoutService.ngZone.run(() => observer.next(value)),
                        error: error => observer.error(error),
                        complete: () => observer.complete()
                    })
                ),
            // Ensures that everything is cleared out on destroy.
            takeUntil(this._destroyed)
        );
    }
}

function parseId(value: string) {
    const PLAYER_ID_REGEXP = /^.*((youtu.be\/)|(vi\/)|(\/u\/\w\/)|(embed\/)|(watch\?)|\?vi?=|&vi?=)([^#&?]*).*/;

    if (value && value.length === 11) {
        return value;
    } else if (value) {
        const match = value.match(PLAYER_ID_REGEXP);

        return match && match[7].length === 11 ? match[7] : value;
    }

    return value;
}

/** Listens to changes from the suggested quality and sets it on the given player. */
function bindSuggestedQualityToPlayer(
    playerObs: Observable<YT.Player | undefined>,
    suggestedQualityObs: Observable<YT.SuggestedVideoQuality | undefined>
) {
    return combineLatest([playerObs, suggestedQualityObs]).subscribe(
        ([player, suggestedQuality]) => player && suggestedQuality && player.setPlaybackQuality(suggestedQuality)
    );
}

/**
 * Returns an observable that emits the loaded player once it's ready. Certain properties/methods
 * won't be available until the iframe finishes loading.
 * @param onAbort Callback function that will be invoked if the player loading was aborted before
 * it was able to complete. Can be used to clean up any loose references.
 */
function waitUntilReady(
    onAbort: (player: UninitializedPlayer) => void
): OperatorFunction<UninitializedPlayer | undefined, Player | undefined> {
    return mergeMap(player => {
        if (!player) {
            return observableOf<Player | undefined>(undefined);
        }
        if (playerIsReady(player)) {
            return observableOf(player as Player);
        }

        // Since removeEventListener is not on Player when it's initialized, we can't use fromEvent.
        // The player is not initialized fully until the ready is called.
        return new Observable<Player>(emitter => {
            let aborted = false;
            let resolved = false;
            const onReady = (event: YT.PlayerEvent) => {
                resolved = true;

                if (!aborted) {
                    event.target.removeEventListener('onReady', onReady);
                    emitter.next(event.target);
                }
            };

            player.addEventListener('onReady', onReady);

            return () => {
                aborted = true;

                if (!resolved) {
                    onAbort(player);
                }
            };
        }).pipe(take(1), startWith(undefined));
    });
}

/** Create an observable for the player based on the given options. */
function createPlayerObservable(
    youtubeContainer: Observable<HTMLElement>,
    videoIdObs: Observable<string | undefined>,
    iframeApiAvailableObs: Observable<boolean>,
    playerVarsObs: Observable<YT.PlayerVars | undefined>,
    ngZone: NgZone
): Observable<UninitializedPlayer | undefined> {
    const playerOptions = combineLatest([videoIdObs, playerVarsObs]).pipe(
        withLatestFrom(combineLatest([videoIdObs, playerVarsObs])),
        map(([constructorOptions]) => {
            const [videoId, playerVars] = constructorOptions;
            return videoId ? { videoId, playerVars } : undefined;
        })
    );

    return combineLatest([youtubeContainer, playerOptions, of(ngZone)]).pipe(
        skipUntilRememberLatest(iframeApiAvailableObs),
        scan(syncPlayerState, undefined),
        distinctUntilChanged()
    );
}

/** Skips the given observable until the other observable emits true, then emit the latest. */
function skipUntilRememberLatest<T>(notifier: Observable<boolean>): MonoTypeOperatorFunction<T> {
    return pipe(
        combineLatestOp(notifier),
        skipWhile(([_, doneSkipping]) => !doneSkipping),
        map(([value]) => value)
    );
}

/** Destroy the player if there are no options, or create the player if there are options. */
function syncPlayerState(
    player: UninitializedPlayer | undefined,
    [container, videoOptions, ngZone]: [HTMLElement, YT.PlayerOptions | undefined, NgZone]
): UninitializedPlayer | undefined {
    if (player && videoOptions && player.playerVars !== videoOptions.playerVars) {
        // The player needs to be recreated if the playerVars are different.
        player.destroy();
    } else if (!videoOptions) {
        if (player) {
            // Destroy the player if the videoId was removed.
            player.destroy();
        }
        return;
    } else if (player) {
        return player;
    }

    // Important! We need to create the Player object outside of the `NgZone`, because it kicks
    // off a 250ms setInterval which will continually trigger change detection if we don't.
    const newPlayer: UninitializedPlayer = ngZone.runOutsideAngular(() => new YT.Player(container, videoOptions));
    newPlayer.videoId = videoOptions.videoId;
    newPlayer.playerVars = videoOptions.playerVars;
    return newPlayer;
}

/**
 * Call cueVideoById if the videoId changes, or when start or end seconds change. cueVideoById will
 * change the loaded video id to the given videoId, and set the start and end times to the given
 * start/end seconds.
 */
function bindCueVideoCall(
    playerObs: Observable<Player | undefined>,
    videoIdObs: Observable<string | undefined>,
    startSecondsObs: Observable<number | undefined>,
    endSecondsObs: Observable<number | undefined>,
    suggestedQualityObs: Observable<YT.SuggestedVideoQuality | undefined>,
    destroyed: Observable<void>
) {
    const cueOptionsObs = combineLatest([startSecondsObs, endSecondsObs]).pipe(
        map(([startSeconds, endSeconds]) => ({ startSeconds, endSeconds }))
    );

    // Only respond to changes in cue options if the player is not running.
    const filteredCueOptions = cueOptionsObs.pipe(filterOnOther(playerObs, player => !!player && !hasPlayerStarted(player)));

    // If the video id changed, there's no reason to run 'cue' unless the player
    // was initialized with a different video id.
    const changedVideoId = videoIdObs.pipe(filterOnOther(playerObs, (player, videoId) => !!player && player.videoId !== videoId));

    // If the player changed, there's no reason to run 'cue' unless there are cue options.
    const changedPlayer = playerObs.pipe(
        filterOnOther(
            combineLatest([videoIdObs, cueOptionsObs]),
            ([videoId, cueOptions], player) =>
                !!player && (videoId != player.videoId || !!cueOptions.startSeconds || !!cueOptions.endSeconds)
        )
    );

    merge(changedPlayer, changedVideoId, filteredCueOptions)
        .pipe(
            withLatestFrom(combineLatest([playerObs, videoIdObs, cueOptionsObs, suggestedQualityObs])),
            map(([_, values]) => values),
            takeUntil(destroyed)
        )
        .subscribe(([player, videoId, cueOptions, suggestedQuality]) => {
            if (!videoId || !player) {
                return;
            }
            player.videoId = videoId;
            player.cueVideoById({
                videoId,
                suggestedQuality,
                ...cueOptions
            });
        });
}

function hasPlayerStarted(player: YT.Player): boolean {
    const state = player.getPlayerState();
    return state !== YT.PlayerState.UNSTARTED && state !== YT.PlayerState.CUED;
}

function playerIsReady(player: UninitializedPlayer): player is Player {
    return 'getPlayerStatus' in player;
}

/** Combines the two observables temporarily for the filter function. */
function filterOnOther<R, T>(otherObs: Observable<T>, filterFn: (t: T, r?: R) => boolean): MonoTypeOperatorFunction<R> {
    return pipe(
        withLatestFrom(otherObs),
        filter(([value, other]) => filterFn(other, value)),
        map(([value]) => value)
    );
}
